import React, {useEffect, useState} from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {PaginationProvider} from 'react-bootstrap-table2-paginator';
import {Pagination} from "./pagination/Pagination";
import {NoRecordsFoundMessage, PleaseWaitMessage} from "./pagination/TablePaginationHelpers";
import {genericGetAll} from "../crud";
import {sizePerPageList} from "../InitialStates";
import {PermissionInfo} from "../../roles-permissions/PermissionInfo";
import {Formik} from "formik";
import {AGCStaticSelect} from "../../../generic-components/AGCStaticSelect";
import {AGCText} from "../../../generic-components/AGCText";
import {Modal, Button} from "react-bootstrap";
import {useThemeMode, systemMode} from "../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider";

// Inner component to handle form and watch for value changes
function FilterForm({
    formikProps,
    searchFields,
    filters,
    colSize,
    tableFilterData,
    setTableFilterData,
    sizePerPage,
    setPage,
    fixedFilterColumn,
    fixedFilterData,
    setFilterValues
}) {
    const {values} = formikProps;

    // Watch for form value changes and update table filters
    useEffect(() => {
        // Persist filter values in parent state
        setFilterValues(values);

        const newFilterData = {...tableFilterData};

        newFilterData.searchString = values.searchText || "";
        newFilterData.searchField = searchFields.map(f => f.dataField).join(",");

        const filterCols = [];
        const filterVals = [];

        if (fixedFilterColumn) {
            filterCols.push(fixedFilterColumn);
            filterVals.push(fixedFilterData);
        }

        filters.forEach(filter => {
            const val = values[filter.dataField];
            if (val) {
                if (filter.type === 'text') {
                    filterCols.push(filter.dataField);
                    filterVals.push(`%${val}%`);
                } else if (val.id) {
                    filterCols.push(filter.dataField);
                    filterVals.push(val.id);
                }
            }
        });

        if (filterCols.length > 0) {
            newFilterData.fixedFilterColumn = filterCols.join(",");
            newFilterData.fixedFilterData = filterVals.join(",");
        } else {
            delete newFilterData.fixedFilterColumn;
            delete newFilterData.fixedFilterData;
        }

        newFilterData.page = 1;
        newFilterData.sizePerPage = sizePerPage;
        newFilterData.currentIndex = 0;

        setPage(1);
        setTableFilterData(newFilterData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    return (
        <form onSubmit={formikProps.handleSubmit} className="form form-label-right px-4 py-10">
            <div className="form-group row">
                {searchFields.length > 0 && (
                    <div className={`col-lg-${colSize} mb-3`}>
                        <AGCText
                            formik={formikProps}
                            name="Search"
                            id="searchText"
                            placeHolder={`Search by ${searchFields.map(f => f.text).join(", ")}...`}
                            value={values.searchText}
                            required={false}
                            toolTip={`Search on ${searchFields.map(f => f.text).join(", ")}`}
                        />
                    </div>
                )}
                {filters.map((filter) => (
                    <div key={filter.dataField} className={`col-lg-${colSize} mb-3`}>
                        {filter.type === 'text' ? (
                            <AGCText
                                formik={formikProps}
                                name={filter.text}
                                id={filter.dataField}
                                placeHolder={`Enter ${filter.text}...`}
                                value={values[filter.dataField]}
                                required={false}
                                toolTip={`Filter by ${filter.text}`}
                            />
                        ) : (
                            <AGCStaticSelect
                                formik={formikProps}
                                isMulti={false}
                                id={filter.dataField}
                                name={filter.text}
                                data={filter.options}
                                value={values[filter.dataField]}
                                required={false}
                                toolTip={`Filter by ${filter.text}`}
                            />
                        )}
                    </div>
                ))}
            </div>
        </form>
    );
}

export function GenericTable({reload, defaultData, columns, entityId, idColumn = 'id', fixedFilterData = null, fixedFilterColumn = null, pageSize = 10, filters = []}) {
    const [dataList, setDataList] = useState(defaultData);
    const [isLoading, setIsLoading] = useState(false);
    const [sizePerPage, setSizePerPage] = useState(pageSize);
    const [page, setPage] = useState(1);
    const [searchFields, setSearchFields] = useState([]);
    const [tableFilterData, setTableFilterData] = useState({});
    const [filterValues, setFilterValues] = useState(() => {
        return {
            searchText: "",
            ...filters.reduce((acc, filter) => ({...acc, [filter.dataField]: ""}), {})
        };
    });

    const [showActionModal, setShowActionModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

    const processedColumns = columns.map(col => {
        let newCol = { ...col };
        if (col.dataField === 'action' || col.showInMobile === false) {
             newCol.headerClasses = `${col.headerClasses || ''} d-none d-md-table-cell`;
             newCol.classes = `${col.classes || ''} d-none d-md-table-cell`;
        }
        return newCol;
    });

    const rowEvents = {
        onClick: (e, row, rowIndex) => {
            if (window.innerWidth < 768) {
                const actionCol = columns.find(c => c.dataField === 'action');
                if (actionCol) {
                    setSelectedRow(row);
                    setShowActionModal(true);
                }
            }
        }
    };

    const rowClasses = (row, rowIndex) => {
        if (window.innerWidth < 768) {
            return "cursor-pointer";
        }
        return "";
    };

    useEffect(() => {
        setSearchFields(getSearchableColumns());
    }, [columns]);

    useEffect(() => {

        setIsLoading(true);
        tableFilterData.page = page;
        tableFilterData.sizePerPage = sizePerPage;
        tableFilterData.currentIndex = 0;

        if (fixedFilterColumn) {
            tableFilterData.fixedFilterData = fixedFilterData;
            tableFilterData.fixedFilterColumn = fixedFilterColumn;
        }

        tableFilterData.sortBy = "updatedOn";


        genericGetAll(entityId, tableFilterData).then(
            ({data}) => {
                setDataList(data);
                setIsLoading(false);
                setPage(1);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, [reload, tableFilterData, entityId]);

    const handleTableChange = (filterData, newState) => {

        filterData = newState.filters;

        filterData.sizePerPage = newState.sizePerPage;

        if (filterData.page === undefined) {
            filterData.page = newState.page;
        }

        if (filterData.page === undefined) {
            filterData.page = 1;
        }

        if (filterData.page === 0) {
            filterData.page = 1;
        }

        if (filterData.sizePerPage === undefined) {
            filterData.sizePerPage = newState.sizePerPage;
        }

        const currentIndex = (filterData.page - 1) * filterData.sizePerPage;

        setIsLoading(true);
        tableFilterData.page = filterData.page;
        tableFilterData.sizePerPage = filterData.sizePerPage;
        tableFilterData.currentIndex = currentIndex;

        genericGetAll(entityId, tableFilterData).then().then(
            ({data}) => {
                setDataList(data);
                setSizePerPage(filterData.sizePerPage);
                setIsLoading(false);
                setPage(filterData.page);
            })
            .catch(() => {
                setIsLoading(false);
            });
    };

    const paginationOptions = {
        custom: true,
        totalSize: dataList.totalCount,
        sizePerPageList: sizePerPageList,
        sizePerPage: sizePerPage,
        page: page,
    };

    function getSearchableColumns() {
        return columns.filter(c => c.search === true);
    }

    const calculateColSize = () => {
        const totalItems = (searchFields.length > 0 ? 1 : 0) + filters.length;
        if (totalItems === 0) return 12;
        const size = Math.floor(12 / totalItems);
        return size < 3 ? 3 : size;
    };

    const colSize = calculateColSize();

    return (
        <>
            {(searchFields.length > 0 || filters.length > 0) &&
            <Formik
                initialValues={filterValues}
                onSubmit={() => {}}>
                {(formikProps) => (
                    <FilterForm
                        formikProps={formikProps}
                        searchFields={searchFields}
                        filters={filters}
                        colSize={colSize}
                        tableFilterData={tableFilterData}
                        setTableFilterData={setTableFilterData}
                        sizePerPage={sizePerPage}
                        setPage={setPage}
                        fixedFilterColumn={fixedFilterColumn}
                        fixedFilterData={fixedFilterData}
                        setFilterValues={setFilterValues}
                    />
                )}
            </Formik>
            }

            <PaginationProvider pagination={paginationFactory(paginationOptions)}>
                {({paginationProps, paginationTableProps}) => {
                    return (
                        <Pagination

                            isLoading={isLoading}
                            paginationProps={paginationProps}>
                            <BootstrapTable
                                bordered={false}
                                wrapperClasses="table-responsive table align-middle table-row-bordered table-row-solid gy-4 gs-9"
                                headerClasses="fs-5 fs-8 fw-bold text-gray-400 text-uppercase"
                                bodyClasses="fw-6 fw-semibold text-gray-600 align-middle"
                                bootstrap4
                                remote
                                keyField={idColumn}
                                data={dataList.entities === null ? [] : dataList.entities}
                                columns={processedColumns}
                                rowEvents={rowEvents}
                                rowClasses={rowClasses}
                                onTableChange={handleTableChange}
                                {...paginationTableProps}>
                                <PleaseWaitMessage entities={dataList.entities}/>
                                <NoRecordsFoundMessage entities={dataList.entities}/>
                            </BootstrapTable>
                        </Pagination>
                    );
                }}
            </PaginationProvider>

            <PermissionInfo entityId={entityId}/>

            <Modal
                show={showActionModal}
                onHide={() => setShowActionModal(false)}
                centered
                contentClassName="shadow-lg rounded"
            >
                <div style={isDarkMode ? {
                    backgroundColor: '#1e1e2d',
                } : {}}>
                    <Modal.Header
                        className="border-0 pb-0 justify-content-center"
                        style={isDarkMode ? { backgroundColor: '#1e1e2d' } : {}}
                    >
                        <Modal.Title className="fw-bolder text-center" style={isDarkMode ? { color: '#a1a5b7' } : {}}>Actions</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-0" style={isDarkMode ? { backgroundColor: '#1e1e2d' } : {}}>
                    <div className="list-group list-group-flush my-2 px-6">
                        {selectedRow && (() => {
                            const actionCol = columns.find(c => c.dataField === 'action');
                            if (actionCol && actionCol.formatExtraData && actionCol.formatExtraData.buttons) {
                                return actionCol.formatExtraData.buttons.map((button) => (
                                    <button
                                        key={button.name}
                                        type="button"
                                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-4 px-7 border-0 mb-3 rounded-3"
                                        style={isDarkMode ? {
                                            backgroundColor: '#252536',
                                            borderColor: '#2b2b40',
                                            color: '#a1a5b7'
                                        } : {
                                            backgroundColor: '#f9f9f9'
                                        }}
                                        onClick={() => {
                                            button.method(selectedRow);
                                            setShowActionModal(false);
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div className="symbol symbol-40px me-4">
                                                <div className={`symbol-label bg-light-${button.color}`}>
                                                    <i className={`fa fa-${button.icon} text-${button.color} fs-3`} />
                                                </div>
                                            </div>
                                            <span className="text-hover-primary fs-6 fw-bold text-start" style={isDarkMode ? { color: '#a1a5b7' } : {}}>
                                                {button.name}
                                            </span>
                                        </div>
                                        <i className="fa fa-chevron-right fs-7" style={isDarkMode ? { color: '#6c7293' } : {}} />
                                    </button>
                                ));
                            }
                            if (actionCol && actionCol.formatter) {
                                return (
                                    <div className="p-10 text-center">
                                        {actionCol.formatter(null, selectedRow, 0, actionCol.formatExtraData)}
                                    </div>
                                );
                            }
                            return <div className="p-10 text-center" style={isDarkMode ? { color: '#a1a5b7' } : {}}>No actions available</div>;
                        })()}
                    </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0" style={isDarkMode ? { backgroundColor: '#1e1e2d' } : {}}>
                        <Button
                            variant={isDarkMode ? 'secondary' : 'light'}
                            className="w-100 fw-bold"
                            onClick={() => setShowActionModal(false)}
                            style={isDarkMode ? { backgroundColor: '#2b2b40', borderColor: '#2b2b40', color: '#a1a5b7' } : { backgroundColor: '#f9f9f9', border: 'none' }}
                        >
                            Cancel
                        </Button>
                    </Modal.Footer>
                </div>
            </Modal>
        </>
    );
}